import React, { Fragment } from 'react';
import { AiIntelReport } from '../data/store';
interface IntelReportPreviewProps {
  data: AiIntelReport;
  reportId: string;
  region: string;
  sender: string;
}
// Render text that may contain ==highlighted== spans produced by the analyst's
// Highlight tool, turning each into a marked span while preserving line breaks.
function renderMarked(text: string): React.ReactNode {
  if (!text) return '-';
  const parts = text.split(/(==[\s\S]*?==)/g);
  return parts.map((part, i) => {
    const m = part.match(/^==([\s\S]*?)==$/);
    if (m) {
      return <mark key={i} className="bg-yellow-400/30 text-yellow-100 rounded px-0.5" data-id="element-331">
          {m[1]}
        </mark>;
    }
    return <Fragment key={i} data-id="element-332">{part}</Fragment>;
  });
}
export function IntelReportPreview({
  data,
  reportId,
  region,
  sender
}: IntelReportPreviewProps) {
  return <div className="bg-[#0f0f0f] border border-border/30 shadow-2xl mx-auto p-8 min-h-[800px] w-full max-w-[210mm] text-foreground/90 relative" data-id="element-333">
      {/* Watermark */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden opacity-[0.03]" data-id="element-334">
        <span className="text-9xl font-bold tracking-widest uppercase rotate-[-45deg] whitespace-nowrap" data-id="element-335">
          {data.classification}
        </span>
      </div>

      <div className="relative z-10" data-id="element-336">
        {/* Header */}
        <div className="text-center border-b-2 border-foreground/20 pb-4 mb-6" data-id="element-337">
          <h2 className="text-lg font-bold tracking-widest uppercase text-destructive" data-id="element-338">
            {data.classification}
          </h2>
          <h1 className="text-2xl font-bold mt-2 tracking-widest" data-id="element-339">
            LAPORAN INTELIJEN
          </h1>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-12 gap-4 mb-8 text-sm font-mono" data-id="element-340">
          <div className="col-span-2 text-muted-foreground" data-id="element-341">Nomor</div>
          <div className="col-span-10" data-id="element-342">
            : {data.nomor || `INTEL/${reportId}/2026`}
          </div>

          <div className="col-span-2 text-muted-foreground" data-id="element-343">Tanggal</div>
          <div className="col-span-10" data-id="element-344">: {data.tanggal}</div>

          <div className="col-span-2 text-muted-foreground" data-id="element-345">Kepada</div>
          <div className="col-span-10" data-id="element-346">: Yth. Kepala BINDA {region}</div>

          <div className="col-span-2 text-muted-foreground" data-id="element-347">Dari</div>
          <div className="col-span-10" data-id="element-348">: Analis Intelijen Wilayah</div>

          <div className="col-span-2 text-muted-foreground" data-id="element-349">Perihal</div>
          <div className="col-span-10 font-bold uppercase" data-id="element-350">
            : {data.subject}
          </div>
        </div>

        <div className="w-full h-px bg-foreground/20 mb-8" data-id="element-351" />

        {/* Sections */}
        <div className="space-y-6 text-sm leading-relaxed text-justify" data-id="element-352">
          <section data-id="element-353">
            <h3 className="font-bold uppercase mb-2" data-id="element-354">I. FAKTA-FAKTA</h3>
            <div className="whitespace-pre-wrap pl-4 border-l-2 border-border/30" data-id="element-355">
              {renderMarked(data.facts)}
            </div>
          </section>

          <section data-id="element-356">
            <h3 className="font-bold uppercase mb-2" data-id="element-357">II. ANALISIS</h3>
            <div className="whitespace-pre-wrap pl-4 border-l-2 border-border/30" data-id="element-358">
              {renderMarked(data.assessment || data.situationSummary)}
            </div>
          </section>

          <section data-id="element-359">
            <h3 className="font-bold uppercase mb-2" data-id="element-360">III. POTENSI DAMPAK</h3>
            <div className="whitespace-pre-wrap pl-4 border-l-2 border-border/30" data-id="element-361">
              {renderMarked(data.potentialImpact)}
            </div>
          </section>

          <section data-id="element-362">
            <h3 className="font-bold uppercase mb-2" data-id="element-363">
              IV. PERKIRAAN PERKEMBANGAN
            </h3>
            <div className="whitespace-pre-wrap pl-4 border-l-2 border-border/30" data-id="element-364">
              {renderMarked(Array.isArray(data.earlyWarning) ? data.earlyWarning.join('\n\n') : data.earlyWarning)}
            </div>
          </section>

          <section data-id="element-365">
            <h3 className="font-bold uppercase mb-2" data-id="element-366">V. REKOMENDASI</h3>
            <div className="whitespace-pre-wrap pl-4 border-l-2 border-border/30" data-id="element-367">
              {renderMarked(data.recommendations)}
            </div>
          </section>
        </div>

        {/* Authentication */}
        <div className="mt-16 pt-8 flex justify-end" data-id="element-368">
          <div className="text-left text-sm min-w-[250px]" data-id="element-369">
            <p className="mb-16 text-right" data-id="element-370">
              {region}, {data.tanggal}
            </p>
            <div className="space-y-1 font-mono text-xs" data-id="element-371">
              <div className="flex border-b border-foreground/20 pb-1 mb-2" data-id="element-372">
                <span className="w-24 text-muted-foreground uppercase" data-id="element-373">
                  Nama Analis
                </span>
                <span className="font-bold" data-id="element-374">
                  :{' '}
                  {data.authentication.split('—')[0]?.trim() || 'Analis Utama'}
                </span>
              </div>
              <div className="flex" data-id="element-375">
                <span className="w-24 text-muted-foreground uppercase" data-id="element-376">
                  Jabatan
                </span>
                <span data-id="element-377">: Analis Intelijen Wilayah</span>
              </div>
              <div className="flex" data-id="element-378">
                <span className="w-24 text-muted-foreground uppercase" data-id="element-379">
                  Unit
                </span>
                <span data-id="element-380">: Sub-Direktorat Analisis & Evaluasi</span>
              </div>
              <div className="flex" data-id="element-381">
                <span className="w-24 text-muted-foreground uppercase" data-id="element-382">
                  BINDA
                </span>
                <span data-id="element-383">: BINDA {region}</span>
              </div>
              <div className="flex" data-id="element-384">
                <span className="w-24 text-muted-foreground uppercase" data-id="element-385">
                  Tanggal
                </span>
                <span data-id="element-386">: {data.tanggal}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center border-t-2 border-foreground/20 pt-4" data-id="element-387">
          <h2 className="text-lg font-bold tracking-widest uppercase text-destructive" data-id="element-388">
            {data.classification}
          </h2>
        </div>
      </div>
    </div>;
}